import * as React from 'react'

import { useFormContext, useFormState } from '@circlefin/form'
import { FieldType, RfiFieldStatus } from '@shared/graphql'
import get from 'lodash.get'

import { useCountryRestricted } from '../../hooks/countryRestricted'
import { useL3Onboarding } from '../../hooks/l3-onboarding'
import { EditableGroups } from '../../hooks/l3-onboarding/constants'
import { RfiModuleStatus } from '../../hooks/l3-onboarding/context'

import type {
  L3OnboardingFieldNameType,
  L3OnboardingRefIdFieldNameType,
} from './types'
import type {
  L3OnboardingFlowGroup,
  L3OnboardingFieldName,
  Jurisdiction,
} from '@shared/graphql'

export interface L3OnboardingControlBaseProps {
  /**
   * Field name.
   */
  name: L3OnboardingFieldNameType
  /**
   * Field name of refId.
   */
  refIdFieldName?: L3OnboardingRefIdFieldNameType
  /**
   * Children.
   */
  children: React.ReactElement<{ disabled?: boolean; warning?: boolean }>
}

/**
 * Revert types to use UseCountryRestrictedProps after refactor.
 * - https://circlepay.atlassian.net/browse/ON-2475.
 */
export type L3OnboardingControlProps = L3OnboardingControlBaseProps & {
  countryIncludes?: Jurisdiction[]
  countryExcludes?: Jurisdiction[]
}

export const L3OnboardingControl: React.FC<L3OnboardingControlProps> = ({
  name,
  children,
  refIdFieldName = 'refId',
  countryIncludes,
  countryExcludes,
}) => {
  const [{ loading, rfiModule, shouldDisableForm }, { getFieldInGroupData }] =
    useL3Onboarding()

  const { watch } = useFormContext()

  const isCountryRestricted = useCountryRestricted(
    countryIncludes != null ? { countryIncludes } : { countryExcludes },
  )
  const refId = watch(refIdFieldName, '') as string
  const rfiUserCommentContent = watch('rfiUserCommentContent', '') as string
  const { dirtyFields } = useFormState()

  const isDirty = Boolean(get(dirtyFields, name))

  // handle react hook form index based names
  const nameParts = name.split('.')
  const fieldName = nameParts[nameParts.length - 1] as L3OnboardingFieldName

  /**
   * Loading: fields will be hidden via <FormLayout>.
   * Country Restricted: field should be hidden for current applicable jurisdiction.
   */
  if (loading || isCountryRestricted) {
    return null
  }

  const fieldAttributes = getFieldInGroupData(fieldName, refId)

  /**
   * Field not found.
   */
  if (!fieldAttributes) {
    return null
  }

  const isQuestionBank = fieldAttributes.type === FieldType.question_bank
  const questionBankVisible =
    fieldAttributes.rfiId != null &&
    fieldAttributes.rfiStatus !== RfiFieldStatus.NOT_ASKED

  /**
   * Only show Question Bank fields if:
   * - Field was asked during RFI.
   */
  if (isQuestionBank && !questionBankVisible) {
    return null
  }

  /**
   * Handle RFI fields.
   */
  if (
    rfiModule.status === RfiModuleStatus.inProgress ||
    rfiModule.status === RfiModuleStatus.responded
  ) {
    const { rfiStatus, readOnly, groupName, type } = fieldAttributes
    /**
     * Field is under rfi if:
     * - Field has been marked for rfi.
     */
    const isUnderRfi =
      !readOnly && rfiStatus != null && rfiStatus !== RfiFieldStatus.NOT_ASKED

    /**
     * Field is always unlocked if:
     * - Field belongs to editable group.
     */
    const isUnlockedField = EditableGroups.includes(
      groupName as L3OnboardingFlowGroup,
    )

    /**
     * Display warning state if:
     * - Field is under rfi
     * - Rfi comment box is empty
     * - Field status is PENDING
     * - Field is not dirty.
     */
    const warningState =
      isUnderRfi &&
      rfiStatus === RfiFieldStatus.PENDING &&
      !rfiUserCommentContent &&
      !isDirty

    const isFieldEditable = isUnderRfi || isUnlockedField

    const rfiDataAttributes = {
      // full path to field, e.g. `owners.0.beneficialOwnerLine1`
      // this let's us easily check if the field is valid using `getFieldState('owners.0.beneficialOwnerLine1')`
      'data-field-path': name,
      // TODO: we could refactor to look up some field attributes based on field path/rfi id in future
      'data-field-type': type,
      // current ref id
      'data-ref-id': refId,
      // RFI id may be undefined, but the field could still be editable if we unlock the whole group
      // we can use this to select all of the fields under RFI
      'data-under-rfi': isFieldEditable,
    }

    return (
      <div {...(isFieldEditable && rfiDataAttributes)}>
        {React.cloneElement(children, {
          warning: warningState,
          // Field should be enabled if it under rfi OR is a new entity
          disabled: !isFieldEditable,
        })}
      </div>
    )
  }

  if (rfiModule.status === RfiModuleStatus.notInBundle) {
    return React.cloneElement(children, {
      warning: false,
      disabled: true,
    })
  }

  return React.cloneElement(children, {
    disabled:
      fieldAttributes.readOnly || shouldDisableForm || children.props.disabled,
  })
}
